import { configureStore } from '@reduxjs/toolkit';
// import { setAutoFreeze } from 'immer';
import tabReducer from './slices/tabSlice';
import globalReducer from './slices/globalSlice';
import { tabMiddleware } from './slices/tabActions';

// Prevent Redux/Immer from freezing objects that may still be referenced by React Flow state.
// setAutoFreeze(false);

// Configure the store with tab-based architecture
// Each tab maintains its own isolated state including:
// - feed, misc, data
//
// Usage:
// - dispatch(createTab({ tabId: 'tab-2', tabName: 'My Tab' })) - Create new tab
// - dispatch(setActiveTab('tab-2')) - Switch tabs
// - dispatch(dispatchToTab('tab-1', setFeed_Dict(data))) - Dispatch action to specific tab
// - dispatch(dispatchToActiveTab(setFeed_Dict(data))) - Dispatch to active tab
// - dispatch(resetTab('tab-1')) - Reset specific tab
// - dispatch(resetAllTabs()) - Reset all tabs
// - dispatch(resetToInitialState()) - Reset entire tabs state

export const localStore = configureStore({
  reducer: {
    tabs: tabReducer,
    global: globalReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Disable for complex nested state
      immutableCheck: false,
    }).concat(tabMiddleware),
});

// Re-export tab actions for convenience
export {
  createTab,
  removeTab,
  setActiveTab,
  renameTab,
  resetTab,
  resetAllTabs,
  resetToInitialState,
  duplicateTab,
  updateTabCanvas,
  updateActiveTabCanvas,
  setActiveTabFlowSheetExecuted,
  setTabFlowSheetExecuted,
  setCanvasChecked,
  setCanvasStreamNumDict,
  setUnitopName,
  setUnitopNames,
  removeUnitopName,
  clearUnitopNames,
  selectActiveTabState,
  selectActiveTabCanvas,
  selectTabCanvas,
  selectAllTabCanvases,
  selectActiveTabFlowSheetExecuted,
  selectTabFlowSheetExecuted,
  selectTabState,
  selectActiveTabId,
  selectTabOrder,
  selectTabNames,
  selectActiveTabUnitopNames,
  selectUnitopName,
  selectTabUnitopNames,
} from './slices/tabSlice';

// Re-export tab action helpers
export { dispatchToTab, dispatchToActiveTab } from './slices/tabActions';

// Re-export global actions
export {
  trackTabModification,
  markTabAsSaved,
  markAllTabsAsSaved,
  updateTabsCount,
  updateAppSettings,
  setLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  initializeSession,
  updateLastActivity,
  resetGlobalState,
  selectLastModifiedInfo,
  selectHasUnsavedChanges,
  selectUnsavedTabIds,
  selectAppSettings,
  selectIsLoading,
  selectNotifications,
  selectSessionInfo,
  // UI Flags - migrated from localStorage
  setUiFlag,
  setLoadCheck,
  setModelOpenDuplicate,
  setEdgeChange,
  setAutoSaveStreamNumber,
  setAppWiz,
  setFocusedColumn,
  resetUiFlags,
  selectUiFlags,
  selectLoadCheck,
  selectModelOpenDuplicate,
  selectEdgeChange,
  selectAutoSaveStreamNumber,
  selectAppWiz,
  selectFocusedColumn,
  // Database version
  setDbVersion,
  selectDbVersion,
  // Canvas clear signal
  setClearCanvasFlag,
  selectClearCanvasFlag,
} from './slices/globalSlice';

// Re-export misc actions and selectors (tab-specific data)
export {
  // Execute/Runtime data (formerly in Context API)
  setExecuteData,
  updateExecuteData,
  setMiscKeyValue,
  setReportData,
  setFlowSheetSolvdData,
  setStreamInputDictContext,
  setRoOuputData,
  clearExecuteRuntimeData,
  selectActiveTabExecuteData,
  selectActiveTabReportData,
  selectActiveTabFlowSheetSolvdData,
  selectActiveTabStreamInputDict,
  selectActiveTabRoOuputData,

  // Equipment dictionaries
  setStripperDict,
  setMixSplitDict,
  setCfDict,
  setHpPumpDict,
  setPumpDict,
  setWorkExcDict,
  setPeltonWheelDict,
  setClroDict,
  setTurboChargerDict,
  setPressExcDict,
  setFlowDict,
  setFlowSheetDict,
  setFlowSheetDictWastage,
  setFlowSheetDictNfWastage,
  setProductFlowDict,
  setMiscFeedFlowDict,
  setStreamNumDict,
  updateStreamNumDict,
  setLocalpath,
  setProjectInfo,
  setStoreNodes,
  setForm,
  setCfOutputDict,
  setCdOutputDict,
  setEdiOutputDict,
  setUnitOpName,
  removeUnitOpName,
  clearUnitOpNames,
  setCurrentUnitWork,
  setPeltonUnitWork,
  setTurboUnitWork,
  setActiveDualequationCCPP,
  setGlobalExitBtn,
  removeMixSplitDictItem,
  resetMiscState,
  selectActiveTabMisc,
  selectActiveTabUnitOpNames,
  selectActiveTabMixSplitDict,
  selectActiveTabFlowSheetDict,
  selectActiveTabStoreNodes,
  selectActiveTabProjectInfo,
  selectActiveTabGlobalExitBtn as selectGlobalExitBtn,
} from './slices/miscSlice';

// Re-export feed actions and selectors
export {
  setFeed_Params,
  resetFeed_Params,
  setFeedParamsinfo,
  resetFeedParamsinfo,
  setFeed_Dict,
  removeFeedDictByKey,
  setFeedFormData,
  resetFeedFormData,
  removeFeedFormDataByKey,
  resetFeedDataSlice,
  setFeedTDS,
  resetFeedTDS,
  setFeedFlowDict,
  resetFeedFlowDict,
  selectActiveTabFeedDict,
  selectActiveTabFeedParams,
  selectActiveTabFeedParamsinfo,
  selectActiveTabFeedForm,
  selectActiveTabFeedTDS,
  selectActiveTabFeedFlowDict,
  selectTabFeedDict,
} from './slices/feedSlice';
